import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin } from "lucide-react";

export default function ContactUs() {
  // Form state
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    message: "",
  });
  const [status, setStatus] = useState({ loading: false, success: null, error: null });

  // Handle changes
  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  // Handle submit to /send-mail
  async function handleSubmit(e) {
    e.preventDefault();
    setStatus({ loading: true, success: null, error: null });

    try {
      const res = await fetch("https://iebcmailer.devyugal.in/send-mail", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus({
          loading: false,
          success:
            "Your message was sent successfully! You should receive a confirmation email shortly.",
          error: null,
        });
        setForm({ firstName: "", lastName: "", email: "", company: "", message: "" });
      } else {
        const text = await res.text();
        setStatus({
          loading: false,
          success: null,
          error:
            text ||
            "Sorry, there was a problem sending your message. Please try again later.",
        });
      }
    } catch (err) {
      setStatus({
        loading: false,
        success: null,
        error:
          "Could not connect to the server. Please check your internet and try again.",
      });
    }
  }

  return (
    <section className="relative bg-gradient-to-br from-gray-50 via-white to-blue-50 py-24 px-6">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-start ">
        {/* LEFT SECTION */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-10"
        >
          <h2 className="text-5xl font-Agatho_Regular font-bold text-gray-900 leading-tight">
            Get in Touch with{" "}
            <span className="text-blue-700">Indo European Business Council</span>
          </h2>

          <p className="text-gray-700 text-lg leading-relaxed max-w-xl">
            Have a question, partnership idea, or want to explore global
            opportunities? We’d love to hear from you. Let’s connect and
            collaborate for impact.
          </p>

          {/* Office Locations */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {[
              {
                country: "United Kingdom",
                address: "First Floor 30 High Road, Wood Green, London N226BX",
                phone: "+44 7405649308",
              },
              {
                country: "Hungary",
                address: "1114 Budapest, Mészöly utca 4 Al. 1. ép.",
                phone: "+36 707201234",
              },
              {
                country: "UAE",
                address:
                  "17th Floor Fahidi Heights, (Near Four Points Sheraton Hotel) Bur Dubai, Dubai.",
                phone: "+971 555067758",
              },
              {
                country: "India",
                // address: "Unitech Cyber Park, Sector-39, Gurugram",
                phone: "+91 9892416044",
              },
            ].map((loc, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -4 }}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition"
              >
                <h4 className="text-xl font-semibold text-gray-900 mb-2">
                  {loc.country}
                </h4>
                {loc.address && (
                  <div className="flex items-start gap-2 text-gray-700 text-base">
                    <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <p className="whitespace-pre-line">{loc.address}</p>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-3">
                  <Phone className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <a
                    href={`tel:${loc.phone.replace(/\s/g, "")}`}
                    className="text-blue-700 hover:underline"
                  >
                    {loc.phone}
                  </a>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Email + Hours */}
          <div className="space-y-4 pt-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-900">Email</h4>
              <a
                href="mailto:iebc@indoeuropeancouncil.eu"
                className="text-blue-700 hover:underline"
              >
                iebc@indoeuropeancouncil.eu
              </a>
            </div>

            <motion.a
              href="mailto:iebc@indoeuropeancouncil.eu"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              className="inline-block mt-6 bg-gradient-to-r from-blue-700 to-blue-600 text-white font-semibold px-10 py-3 rounded-lg shadow-md hover:shadow-lg transition"
            >
              Send Message →
            </motion.a>
          </div>
        </motion.div>

        {/* RIGHT SECTION - FORM */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="my-auto"
        >
          <form
            className="bg-white rounded-2xl shadow-xl p-10 space-y-6"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <h3 className="text-3xl font-Agatho_Regular font-semibold text-gray-900 mb-6">
              Send Us a Message
            </h3>

            <div className="flex flex-col sm:flex-row gap-6">
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                required
                value={form.firstName}
                onChange={handleChange}
                className="flex-1 border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-600"
                autoComplete="given-name"
              />
              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                required
                value={form.lastName}
                onChange={handleChange}
                className="flex-1 border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-600"
                autoComplete="family-name"
              />
            </div>

            <input
              type="email"
              name="email"
              placeholder="Email Address"
              required
              value={form.email}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-600"
              autoComplete="email"
            />

            <input
              type="text"
              name="company"
              placeholder="Company / Organization (optional)"
              value={form.company}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-600"
              autoComplete="organization"
            />

            <textarea
              name="message"
              rows="5"
              placeholder="Your Message"
              required
              value={form.message}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-600 resize-none"
            ></textarea>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className={`w-full bg-blue-700 text-white font-semibold py-3 rounded-lg hover:bg-blue-800 transition ${
                status.loading ? "opacity-80 cursor-not-allowed" : ""
              }`}
              disabled={status.loading}
            >
              {status.loading ? "Submitting..." : "Submit Inquiry"}
            </motion.button>
            {status.error && (
              <div className="text-red-600 mt-2 text-sm">{status.error}</div>
            )}
            {status.success && (
              <div className="text-green-700 mt-2 text-sm">{status.success}</div>
            )}
          </form>
        </motion.div>
      </div>
    </section>
  );
}
